import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import LeftMenu from "../components/LeftMenu";
import { ROUTES } from "../routes";
import CalendarWeekView from "../components/calendar/CalendarWeekView";
import CalendarMonthView from "../components/calendar/CalendarMonthView";
import CalendarSkeleton from "../components/calendar/CalendarSkeleton";
import CalendarPageHeader from "../components/calendar/CalendarPageHeader";
import CalendarEmptyPeriod from "../components/calendar/CalendarEmptyPeriod";
import CloseDayDialog from "../components/calendar/CloseDayDialog";
import { fetchCalendar } from "../features/calendar/slice-extensions/calendar-slice-extension";
import { useCalendarViewState } from "../features/calendar/use-calendar-view-state";
import { useOccurrenceDrag } from "../features/calendar/use-occurrence-drag";
import { useCalendarFreeSlots } from "../features/calendar/use-calendar-free-slots";
import { useClosedDays } from "../features/closed-days/use-closed-days";
import {
  selectCalendarError,
  selectCalendarInstructorFilter,
  selectCalendarItems,
  selectCalendarStatus,
  setInstructorFilter,
} from "../features/calendar/calendar-slice";
import { fetchColleagues } from "../features/instructors/slice-extensions/instructors-slice-extension";
import {
  selectActiveColleagues,
  selectColleaguesInitializationStatus,
} from "../features/instructors/instructors-slice";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import { selectAbsencesRange } from "../features/absences/absences-slice";
import { fetchAbsencesRange } from "../features/absences/slice-extensions/absences-slice-extension";
import { addDays, toYmd } from "../features/calendar/utils/calendar-range";
import { useWeekStartPreference } from "../features/calendar/use-week-start-preference";

/** The instructor's main scheduling surface: occurrences across all sessions. */
const CalendarPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const items = useAppSelector(selectCalendarItems);
  const lastError = useAppSelector(selectCalendarError);
  const status = useAppSelector(selectCalendarStatus);
  const instructorFilter = useAppSelector(selectCalendarInstructorFilter);
  const activeColleagues = useAppSelector(selectActiveColleagues);
  const colleaguesInitStatus = useAppSelector(selectColleaguesInitializationStatus);
  // Week layout follows the instructor's preference; Monday until it loads.
  const weekStart = useWeekStartPreference();

  const { anchor, view, from, to, rangeLabel, setView, step, goToToday, drillDownToWeek } =
    useCalendarViewState(weekStart);

  useEffect(() => {
    dispatch(fetchCalendar({ from, to, instructorId: instructorFilter ?? undefined }));
    // Declared member absences overlay the month view as display-only day counts.
    dispatch(fetchAbsencesRange({ from, to }));
  }, [dispatch, from, to, instructorFilter]);

  const absences = useAppSelector(selectAbsencesRange);
  const absenceCountByDate = useMemo(() => {
    const counts = new Map<string, number>();
    for (const absence of absences) {
      let cursor = new Date(`${absence.startDate}T00:00:00`);
      const end = new Date(`${absence.endDate}T00:00:00`);
      while (cursor <= end) {
        const key = toYmd(cursor);
        counts.set(key, (counts.get(key) ?? 0) + 1);
        cursor = addDays(cursor, 1);
      }
    }
    return counts;
  }, [absences]);

  // The instructor filter (and the per-instructor chip accents) need the roster.
  useEffect(() => {
    if (colleaguesInitStatus === "not-initialized") {
      dispatch(fetchColleagues());
    }
  }, [dispatch, colleaguesInitStatus]);

  const reloadCalendar = () =>
    dispatch(fetchCalendar({ from, to, instructorId: instructorFilter ?? undefined }));

  const { closedByDate, manage, busy, openManage, closeManage, submitClose, submitReopen } =
    useClosedDays(from, to, reloadCalendar);

  // Dragging a chip onto another day reschedules it, keeping its time of day.
  const drag = useOccurrenceDrag(reloadCalendar);
  const freeSlots = useCalendarFreeSlots(view, to);

  /** Schedules onto a specific day (and time, when a free slot was picked). */
  const createSessionOn = (day: Date, time?: string, duration?: number) =>
    navigate(ROUTES.CREATE_SESSION, {
      state: { selectedDate: toYmd(day), startTime: time, duration },
    });

  const showSkeleton = status === LoadingStatus.Loading && items.length === 0;
  const showEmptyPeriod =
    !showSkeleton && status !== LoadingStatus.Loading && items.length === 0;

  return (
    <div className="flex">
      <LeftMenu />
      <div className="flex-1 p-6">
        <CalendarPageHeader
          rangeLabel={rangeLabel}
          view={view}
          items={items}
          closedByDate={closedByDate}
          showSummary={!showSkeleton}
          hasError={!!lastError}
          instructors={activeColleagues}
          instructorFilter={instructorFilter}
          onViewChange={setView}
          onPrevious={() => step(-1)}
          onToday={goToToday}
          onNext={() => step(1)}
          onInstructorFilterChange={(id) => dispatch(setInstructorFilter(id))}
        />
        <div
          className={`mt-4 transition-opacity ${
            (status === LoadingStatus.Loading && !showSkeleton) || drag.isRescheduling
              ? "opacity-60"
              : ""
          }`}
        >
          {showSkeleton ? (
            <CalendarSkeleton view={view} />
          ) : view === "week" ? (
            <CalendarWeekView
              anchor={anchor}
              items={items}
              closedByDate={closedByDate}
              freeSlots={freeSlots}
              drag={drag}
              onManageDay={openManage}
              onCreateSession={createSessionOn}
              weekStart={weekStart}
            />
          ) : (
            <CalendarMonthView
              anchor={anchor}
              items={items}
              closedByDate={closedByDate}
              absenceCountByDate={absenceCountByDate}
              drag={drag}
              onManageDay={openManage}
              onCreateSession={createSessionOn}
              onDayDrillDown={drillDownToWeek}
              weekStart={weekStart}
            />
          )}
        </div>
        {showEmptyPeriod && <CalendarEmptyPeriod />}
      </div>
      {manage && (
        <CloseDayDialog
          day={manage.day}
          closed={manage.closed}
          busy={busy}
          onClose={closeManage}
          onSubmitClose={submitClose}
          onReopen={submitReopen}
        />
      )}
    </div>
  );
};

export default CalendarPage;
